import React from 'react';
import styles from './index.module.scss';

const DescriptionAndSocial = ({ publication }) => {
  const [publicationData, setPublicationData] = React.useState(null);
  const [contentData, setContentData] = React.useState(null);
  const [pathToFolder, setPathToFolder] = React.useState('');

  React.useEffect(() => {
    if (publication) setPublicationData(publication);
  }, [publication]);

  React.useEffect(() => {
    if (!publicationData) return;

    if (publicationData.content) {
      try {
        const parsed = JSON.parse(publicationData.content);
        setContentData(Array.isArray(parsed) ? parsed : []);
      } catch {
        setContentData([]);
      }
    } else {
      setContentData([]);
    }

    if (publicationData.path && publicationData.eng_name) {
      setPathToFolder(`https://${publicationData.path}${publicationData.eng_name}/`);
    }
  }, [publicationData]);

  const normalizeText = React.useCallback((value) => {
    if (typeof value !== 'string') return '';
    const normalized = value.trim();
    if (!normalized || normalized.toLowerCase() === 'none') return '';
    return normalized;
  }, []);

  const viewModel = React.useMemo(() => {
    if (!Array.isArray(contentData) || contentData.length === 0 || !contentData[0]?.block_content) {
      return { text: '', items: [] };
    }

    const blockContent = Array.isArray(contentData[0].block_content) ? contentData[0].block_content : [];

    const textBlock =
      blockContent.find((item) => {
        const type = (item?.cotent_type || item?.content_type || '').toLowerCase();
        const hasText = Boolean(normalizeText(item?.content_text));
        if (!hasText) return false;
        return type === 'text' || type === 'long_text';
      }) ||
      blockContent.find((item) => {
        const type = (item?.cotent_type || item?.content_type || '').toLowerCase();
        const hasText = Boolean(normalizeText(item?.content_text));
        if (!hasText) return false;
        return type !== 'imgs_block' && type !== 'imgs';
      });

    const imgsBlock = blockContent.find(
      (item) =>
        (item?.cotent_type === 'imgs_block' || item?.content_type === 'imgs_block') &&
        item?.imgs_names &&
        item.imgs_names !== 'none',
    );

    const text = normalizeText(textBlock?.content_text);

    if (!imgsBlock) {
      return { text, items: [] };
    }

    let imgNames = [];
    try {
      imgNames = typeof imgsBlock.imgs_names === 'string' ? JSON.parse(imgsBlock.imgs_names) : imgsBlock.imgs_names;
    } catch {
      imgNames =
        typeof imgsBlock.imgs_names === 'string'
          ? imgsBlock.imgs_names.split(',').map((v) => v.trim())
          : [];
    }

    if (!Array.isArray(imgNames)) imgNames = [];

    imgNames = imgNames
      .filter((name) => normalizeText(name))
      .map((name) => (String(name).includes('\\') ? String(name).substring(String(name).lastIndexOf('\\') + 1) : String(name)));

    let signatures = [];
    try {
      signatures =
        typeof imgsBlock.imgs_signatures === 'string'
          ? JSON.parse(imgsBlock.imgs_signatures)
          : imgsBlock.imgs_signatures;
    } catch {
      signatures =
        typeof imgsBlock.imgs_signatures === 'string'
          ? imgsBlock.imgs_signatures.split(',').map((v) => v.trim())
          : [];
    }
    if (!Array.isArray(signatures)) signatures = [];

    const items = imgNames.map((imgName, index) => {
      const rawLink = normalizeText(signatures[index]);
      const href = rawLink ? (rawLink.startsWith('http') ? rawLink : `https://${rawLink}`) : '';
      return { imgName, href };
    });

    return { text, items };
  }, [contentData, normalizeText]);

  const descriptionLines = React.useMemo(
    () =>
      String(viewModel.text || '')
        .split(/\r?\n/)
        .map((line) => line.trim())
        .filter(Boolean),
    [viewModel.text],
  );

  if (!pathToFolder || (!viewModel.text && viewModel.items.length === 0)) {
    return null;
  }

  return (
    <section className={styles.descriptionAndSocial}>
      {descriptionLines.length > 0 && (
        <p className={styles.description}>
          {descriptionLines.map((line, index) => (
            <span key={`${line}-${index}`} className={styles.descriptionLine}>
              {line}
            </span>
          ))}
        </p>
      )}
      {viewModel.items.length > 0 && (
        <div className={styles.socialGrid}>
          {viewModel.items.map((item, index) => {
            const image = (
              <img
                src={`${pathToFolder}${item.imgName}`}
                alt={`social-${index + 1}`}
                className={styles.socialImage}
              />
            );

            return (
              <div key={`${item.imgName}-${index}`} className={styles.socialItem}>
                {item.href ? (
                  <a href={item.href} target='_blank' rel='noopener noreferrer' className={styles.socialLink}>
                    {image}
                  </a>
                ) : (
                  image
                )}
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
};

export default DescriptionAndSocial;
